import { Settings as SettingsIcon, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api, ApiError, SETTINGS_UPDATED_EVENT, type AppSettings, type SettingsResponse } from '../api/client';
import { useAuth } from '../auth';
import { Topbar } from '../components/Layout';
import { PageLoader, Switch, useToast } from '../components/ui';
import { useI18n } from '../i18n';

/** Workspace-wide switches for administrators. */
export function SettingsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const allowed = can('settings.manage');
  const toast = useToast();
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    api.settings().then(setData, (e) => toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error'));
  }, [allowed, toast, t]);

  if (!allowed) return <Navigate to="/" replace />;
  if (!data) return <PageLoader />;

  async function save(patch: Partial<AppSettings>) {
    setSaving(true);
    try {
      setData(await api.updateSettings(patch));
      window.dispatchEvent(new Event(SETTINGS_UPDATED_EVENT));
      toast(t('settings.saved'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  const { settings, server } = data;
  return (
    <>
      <Topbar title={t('settings.title')} />
      <div className="card">
        <div className="card-head">
          <div className="tile">
            <SettingsIcon />
          </div>
          <div className="grow">
            <h2>{t('settings.title')}</h2>
            <div className="sub">{t('settings.sub')}</div>
          </div>
        </div>
        <div className="card-pad stack" style={{ paddingTop: 0 }}>
          <h3 className="row gap-8">
            <Sparkles className="icon-sm" /> {t('settings.assistant')}
          </h3>
          <Switch
            checked={settings.assistantEnabled}
            disabled={saving}
            onChange={(v) => void save({ assistantEnabled: v })}
            label={t('settings.assistant_enabled')}
          />
          <p className="hint">
            {!server.assistantConfigured
              ? t('settings.assistant_not_configured')
              : server.assistantMock
                ? t('settings.assistant_demo')
                : t('settings.assistant_hint')}
          </p>
        </div>
      </div>
    </>
  );
}
